import React, { useState } from "react";
import styles from "./OnboardingMetrics.module.css";
import { useOnboarding } from "../../hooks/useOnboarding";
import BackButton from "../../components/common/BackButton";
import NextButton from "../../components/common/NextButton";
import InlineSpinner from "../../components/common/InlineSpinner";
import { useAuth } from "../../contexts/AuthContext";
import onboardingService from "../../services/onboardingService";

interface OnboardingMetricsProps {
  onBack: () => void;
  onNext: (selected: string[]) => void;
}

type Metric = { key: string; label: string };
type MetricCategory = { name: string; metrics: Metric[] };

const METRIC_CATEGORIES: MetricCategory[] = [
  {
    name: "Financial Health",
    metrics: [
      { key: "revenue_mrr_arr", label: "Revenue (MRR/ARR)" },
      { key: "burn_rate", label: "Burn Rate" },
      { key: "runway", label: "Runway" },
      { key: "revenue_growth_rate", label: "Revenue Growth Rate" },
      { key: "gross_margin", label: "Gross Margin" },
    ],
  },
  {
    name: "Growth & Traction",
    metrics: [
      { key: "active_users_dau_mau", label: "Active Users (DAU/MAU)" },
      { key: "user_growth_rate", label: "User Growth Rate" },
      { key: "conversion_rate", label: "Conversion Rate" },
      { key: "churn_rate", label: "Churn Rate" },
      { key: "nrr", label: "NRR (Net Revenue Retention)" },
    ],
  },
  {
    name: "Customer",
    metrics: [
      { key: "cac", label: "CAC (Customer Acquisition Cost)" },
      { key: "ltv", label: "LTV (Lifetime Value)" },
      { key: "ltv_cac_ratio", label: "LTV:CAC Ratio" },
      { key: "nps", label: "NPS (Net Promoter Score)" },
      { key: "paid_customers", label: "Paid Customers" },
    ],
  },
  {
    name: "Engagement & Product",
    metrics: [
      { key: "feature_adoption", label: "Feature Adoption" },
      { key: "retention_d7_d30", label: "Retention (D7/D30)" },
      { key: "task_completion_rate", label: "Task Completion Rate" },
      { key: "session_frequency", label: "Session Frequency" },
    ],
  },
  {
    name: "Fundraising & Milestones",
    metrics: [
      { key: "capital_raised", label: "Capital Raised to Date" },
      { key: "pipeline_lois", label: "Pipeline / LOIs" },
      { key: "key_hires", label: "Key Hires Made" },
      { key: "partnerships_signed", label: "Partnerships Signed" },
      { key: "waitlist_size", label: "Waitlist Size" },
    ],
  },
  {
    name: "Operational",
    metrics: [
      { key: "deployment_frequency", label: "Deployment Frequency" },
      { key: "uptime_incidents", label: "Uptime / Incident Count" },
      { key: "cycle_time", label: "Cycle Time" },
    ],
  },
];

const OnboardingMetrics: React.FC<OnboardingMetricsProps> = ({
  onBack,
  onNext,
}) => {
  const { user } = useAuth();
  const { data, setMetrics } = useOnboarding();
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(
    data.metrics || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleMetric = (key: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleNext = async () => {
    if (selectedMetrics.length === 0) {
      setError("Please select at least one metric.");
      return;
    }

    if (!user) {
      setError("User not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      setMetrics(selectedMetrics);

      // Save to Supabase onboarding data
      const currentOnboardingData = {
        ...data,
        metrics: selectedMetrics,
        step: 5,
        completed_steps: [...(data.completed_steps || []), "metrics_selection"],
      };

      await onboardingService.updateOnboarding(user.id, currentOnboardingData);
      onNext(selectedMetrics);
    } catch (error) {
      console.error("Failed to save metrics:", error);
      setError("Failed to save your metrics. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <h2 className={styles.header}>
          What metrics are you looking for updates on?
        </h2>
        <p className={styles.subtitle}>
          Select one or more metrics to get personalized insights.
        </p>

        {error && (
          <div
            style={{
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "4px",
              padding: "1rem",
              marginBottom: "1rem",
              color: "#c33",
            }}
          >
            {error}
          </div>
        )}

        <div className={styles.categoriesContainer}>
          {METRIC_CATEGORIES.map((category) => (
            <div key={category.name} className={styles.categorySection}>
              <h3 className={styles.categoryLabel}>{category.name}</h3>
              <div className={styles.pillGrid}>
                {category.metrics.map((metric) => (
                  <button
                    key={metric.key}
                    type="button"
                    className={`${styles.pill} ${
                      selectedMetrics.includes(metric.key) ? styles.selected : ""
                    }`}
                    onClick={() => toggleMetric(metric.key)}
                  >
                    {metric.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.navButtons}>
          <button className={styles.backButton} onClick={onBack}>
            Back
          </button>
          <button
            className={styles.nextButton}
            onClick={handleNext}
            disabled={selectedMetrics.length === 0 || isSubmitting}
          >
            {isSubmitting ? <InlineSpinner /> : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingMetrics;
